import React, {
	useState, useRef, useMemo, useEffect, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
	DndContext, DragOverlay, closestCenter, MouseSensor, useSensor, useSensors,
	DragStartEvent, DragEndEvent,
} from '@dnd-kit/core';
import {
	SortableContext, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { isPlayerSupported } from 'amazon-ivs-player';
import { useMachine } from '@xstate/react';
import { GreenroomState } from '../../stores/types/types';
import * as MediaActions from '../../stores/actions/media';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import ShareWith, { SHARE_TYPE } from '../general-components/share-with';
import Thumbnail from '../chat/chat-document-thumbnail';
import Socket from '../../connection/socket';
import WaitingIndicator from '../general-components/waiting-indicator';
import { DeleteMediaItem, DeleteCloudConvertJob } from '../../connection/media';
import Dialog from '../general-components/dialog';
import ButtonClose from '../general-components/button-close';
import MediaModalButtons from './media-modal-buttons';
import SortablePage from './media-modal-page';
import { checkFileName, getFileExtension } from '../../utils/media';
import { useMediaUpload } from '../../hooks/use-content-upload';
import {
	AUDIO_TYPES,
	CLOUD_CONVERT_STATUS,
	IMG_TYPES,
	MEDIA_TYPES,
	VID_TYPES,
} from '../../types/constants';
import MediaVideo from '../general-components/media-video';
import MediaPresentationFrame from '../main-video/media-presentation-frame';
import usePptxDeckAnimations from '../../hooks/use-pptx-deck-animations';
import {
	pptxDeckNextTarget, pptxDeckPrevTarget, pptxDeckPrevOrResetTarget, pptxDeckSlideHasBuilds,
	pptxDeckIsAddedPage, pptxDeckAlignSlides, PptxDeckPosition,
} from '../../utils/pptx-deck-animations-reconcile';
import { videoMachine, actions, videoActionCommands } from '../../utils/video-machine';
import BlackImage from '../../images/black-image.jpeg';
import * as keyListeners from '../../utils/keyboard-handler';
import useShots from '../../contexts/shots/provider';
import { AddUploadFunction, useGetPageUpload } from '../../contexts/upload-context';
import { FEED_TYPES } from '../../stores/reducers/shots';

const {
	LOADED, FAIL, TIMING, TIMECHANGE, PLAY, END,
} = videoActionCommands;

interface OwnProps {
	socket: Socket | null;
	addUpload: AddUploadFunction;
}

export default function MediaModal(props: OwnProps) {
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const presentedMedia = useSelector((state: GreenroomState) => state.MediaReducer.presentedMedia);
	const selectedMedia = useSelector(
		(state: GreenroomState) => state.MediaReducer.selectedMedia,
	); // the media in the modal
	const selectedPage = useSelector((state: GreenroomState) => state.MediaReducer.selectedPage);
	const idOfCurrentlyUploadingMedia = useSelector(
		(state: GreenroomState) => state.MediaReducer.idOfCurrentlyUploadingMedia,
	);
	const currentlyUploadingInModal = useSelector(
		(state: GreenroomState) => state.MediaReducer.currentlyUploadingInModal,
	);
	const savingMedia = useSelector((state: GreenroomState) => state.MediaReducer.savingMedia);

	const {
		socket,
		addUpload,
	} = props;

	const dispatch = useDispatch();

	// distance constraint lets a plain click on a thumbnail select the page
	// while a deliberate drag (> 5px) starts a reorder
	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
	);

	const [loadingIndicatorVisible, setLoadingIndicatorVisible] = useState<boolean>(false);
	// keyed by the page's image URL (stable across reorders) rather than the
	// page index, so a drag/drop can't leave the highlight on the wrong slide
	const [hoveringOverPageThumb, setHoveringOverPageThumb] = useState<string | undefined>();
	const [shareWithPopoverOpen, setShareWithPopoverOpen] = useState<boolean>(false);
	const [deletePageOpen, setDeletePageOpen] = useState<boolean | undefined>();
	const [isDraggingPage, setIsDraggingPage] = useState(false);
	const [activePageId, setActivePageId] = useState<string | null>(null);
	const [isLoaded, setIsLoaded] = useState(false);
	// 1-indexed animation step within the current (animated) slide. This modal has
	// no synced/presented position (unlike canvas) — it's a private preview — so
	// step is tracked locally rather than through PptxDeckPosition/presentedMedia.
	const [selectedStep, setSelectedStep] = useState<number>(1);

	const fileInput = useRef<HTMLInputElement | null>(null);
	const deleteDialog = useRef<any>(null);
	const pages = useRef<any>({});
	const pagesContainer = useRef<any>(null);
	const uploadButton = useRef<any>(null);
	const thumbnailToDeleteRef = useRef<any>(null);
	const deleteMediaItemBtnRef = useRef<any>(null);
	const mediaTitleInputRef = useRef<any>(null);
	const bitrateDialogRef = useRef<Dialog | null>(null);
	const loadingThumbRef = useRef<any>(null);
	const optionKeyDownRef = useRef<boolean>(false);
	const uriTimeout = useRef<NodeJS.Timeout | null>(null);
	const mediaRef = useRef(media);
	const videoElRef = useRef<HTMLVideoElement>(null);
	const initialLoad = useRef<boolean>(true);
	const start = useRef<number | null>(null);
	const echoCancelledAudioRef = useRef<HTMLAudioElement>(null);

	const [current, send] = useMachine(videoMachine, {
		actions: { ...actions },
	});
	const {
		elapsed,
	} = current.context;

	const [uploading, setUploading] = useState(false);

	const mediaImagesCurrentlyUploading = (
		idOfCurrentlyUploadingMedia === selectedMedia?.cloud_convert_id
		|| selectedMedia?.id === 1
	);

	const { doUploadMedia } = useMediaUpload(socket);
	const { stripFeedFromShots } = useShots();
	const {
		pageUpload,
		progress: uploadProgress,
	} = useGetPageUpload(selectedMedia?.endpoint ?? '');

	useEffect(() => {
		if (pageUpload) {
			setUploading(true);
		}
	}, [pageUpload]);

	const isProcessingCloudConvert = useMemo(
		() => (
			(!!selectedMedia?.cloud_convert_id
			&& selectedMedia?.convert_status === CLOUD_CONVERT_STATUS.IN_PROGRESS)
		),
		[selectedMedia],
	);

	const isUploadingOrProcessing = useMemo(() => {
		if (uploading) {
			return 'Uploading';
		}
		if (isProcessingCloudConvert) {
			return 'Processing';
		}
		return '';
	}, [uploading, isProcessingCloudConvert]);

	function handleKeyDown(event: KeyboardEvent) {
		if (event.keyCode === 18) {
			optionKeyDownRef.current = true;
		}
	}

	function handleKeyUp(event: KeyboardEvent) {
		if (event.keyCode === 18) {
			optionKeyDownRef.current = true;
		}
	}

	useEffect(() => {
		if (videoElRef.current && selectedMedia?.media_data.loop && videoElRef.current.paused) {
			videoElRef.current.play();
		}
	}, [selectedMedia]);

	useEffect(() => {
		// todo is this neccessary now?
		// Used as an easter egg for producers to bypass
		// the auto conversion of high bitrate mp4s (> 4mb/s)
		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('keyup', handleKeyUp);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('keyup', handleKeyUp);
		};
	}, []);

	const arrayOfImageUrls = useMemo(
		() => selectedMedia?.media_data.image_array ?? [],
		[selectedMedia?.media_data.image_array],
	);

	const {
		hasAnimations, contentUrl, slides: pptxSlides, slideOrder: pptxSlideOrder,
	} = usePptxDeckAnimations(selectedMedia?.id ?? -1);
	// A page uploaded onto the deck after conversion renders like a regular media page.
	const showsPageMedia = !hasAnimations || pptxDeckIsAddedPage(pptxSlideOrder, selectedPage);

	// A slide change (prev/next crossing a slide boundary, a thumbnail click, or a
	// drag reorder) always lands on that slide's own start — mirrors canvas, where
	// every slide-to-slide move (pptxDeckNextTarget / pptxDeckPrevOrResetTarget)
	// targets step 1.
	useEffect(() => {
		setSelectedStep(1);
	}, [selectedPage, selectedMedia?.id]);

	const showAnimationIcon = hasAnimations && pptxDeckSlideHasBuilds(pptxSlides, selectedPage);
	const deckTotal = hasAnimations ? pptxSlides.length : arrayOfImageUrls.length;
	const pptxAtStart = hasAnimations
		&& pptxDeckPrevTarget(pptxSlides, selectedPage, selectedStep) === null;
	const pptxAtEnd = hasAnimations
		&& pptxDeckNextTarget(pptxSlides, selectedPage, selectedStep) === null;

	// A pptx build set to auto-play (rather than advance on click) reports its step
	// with no NextStep from us; persist it as the new local position instead of
	// letting the reconciler fight to revert it — mirrors canvas's onAutoAdvance.
	const onPptxDeckAutoAdvance = useCallback((pos: PptxDeckPosition) => {
		dispatch(MediaActions.setSelectedPage(pos.slide));
		setSelectedStep(pos.step);
	}, [dispatch]);

	function pageClicked(page: number) {
		if (page !== selectedPage && !isDraggingPage) {
			setLoadingIndicatorVisible(true);
			if (uriTimeout.current!) {
				setLoadingIndicatorVisible(false);
				clearInterval(uriTimeout.current!);
			}
			dispatch(MediaActions.setSelectedPage(page));
		}
	}

	function previousPageClicked() {
		if (hasAnimations) {
			const prev = pptxDeckPrevOrResetTarget(pptxSlides, selectedPage, selectedStep);
			if (!prev) return;
			if (prev.slide !== selectedPage) {
				dispatch(MediaActions.setSelectedPage(prev.slide));
				setTimeout(() => pages.current[prev.slide - 2]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
				if (prev.slide === 1) {
					setTimeout(() => pagesContainer.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' }));
				}
			}
			setSelectedStep(prev.step);
			return;
		}
		if (selectedPage !== 1) {
			setLoadingIndicatorVisible(true);
			if (uriTimeout.current!) {
				setLoadingIndicatorVisible(false);
				clearInterval(uriTimeout.current!);
			}
			dispatch(MediaActions.setSelectedPage(selectedPage - 1));

			// wait until update ends, then scroll to show previous thumb
			setTimeout(() => pages.current[selectedPage - 3]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));

			// if this is the first thumb, scroll all the way to the top
			if (selectedPage - 1 === 1) {
				setTimeout(() => pagesContainer.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' }));
			}
		}
	}

	function nextPageClicked() {
		if (hasAnimations) {
			const next = pptxDeckNextTarget(pptxSlides, selectedPage, selectedStep);
			if (!next) return;
			if (next.slide !== selectedPage) {
				dispatch(MediaActions.setSelectedPage(next.slide));
				setTimeout(() => pages.current[next.slide]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
				if (next.slide === arrayOfImageUrls.length) {
					setTimeout(() => uploadButton.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
				}
			}
			setSelectedStep(next.step);
			return;
		}
		if (selectedPage !== arrayOfImageUrls.length) {
			setLoadingIndicatorVisible(true);
			if (uriTimeout.current!) {
				setLoadingIndicatorVisible(false);
				clearInterval(uriTimeout.current!);
			}
			dispatch(MediaActions.setSelectedPage(selectedPage + 1));

			// scroll to show the next thumbnail in the view
			setTimeout(() => pages.current[selectedPage + 1]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));

			// if this is the last thumbnail, scroll to the upload button
			if (selectedPage + 1 === arrayOfImageUrls.length) {
				setTimeout(() => uploadButton.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
			}
		}
	}

	function shareWithPopoverToggled(open: boolean) {
		setShareWithPopoverOpen(open);
	}

	function closeModal() {
		dispatch(MediaActions.setMediaModalOpen(false, undefined));
		dispatch(MediaActions.setSelectedPage(1));
	}

	function imageReady() {
		setLoadingIndicatorVisible(false);
		if (uriTimeout.current!) {
			clearInterval(uriTimeout.current!);
		}
	}

	function checkImage(uri: string) {
		return new Promise((resolve) => {
			const img = new Image();
			img.onload = () => resolve(true);
			img.onerror = () => resolve(false);
			img.src = uri;
		});
	}

	async function imageNotReady(imageURL: string) {
		setLoadingIndicatorVisible(true);
		uriTimeout.current = setInterval(async () => {
			const valid = await checkImage(imageURL);
			if (valid) {
				clearInterval(uriTimeout.current!);
				setLoadingIndicatorVisible(false);
			}
		}, 5000);

		// do a first initial check rather than waiting 5 seconds first
		const valid = await checkImage(imageURL);

		if (valid) {
			clearInterval(uriTimeout.current!);
			setLoadingIndicatorVisible(false);
		}
	}

	const currentImageUrl = useMemo(
		() => arrayOfImageUrls[selectedPage - 1],
		[arrayOfImageUrls, selectedPage],
	);

	const onDragStart = (event: DragStartEvent) => {
		setIsDraggingPage(true);
		setActivePageId(String(event.active.id));
		setHoveringOverPageThumb(undefined);
	};

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setIsDraggingPage(false);
		setActivePageId(null);
		// the dragged page lands under the pointer, but no mouseenter fires
		// post-drop, so highlight it explicitly
		setHoveringOverPageThumb(over ? String(active.id) : undefined);
		if (!over || !token || !selectedMedia || !socket || !user || active.id === over.id) return;

		const oldIndex = arrayOfImageUrls.indexOf(String(active.id));
		const newIndex = arrayOfImageUrls.indexOf(String(over.id));
		if (oldIndex === -1 || newIndex === -1) return;

		// the slide currently in view — keep it selected wherever it lands
		const currentlySelectedPage = currentImageUrl;
		const newPages = arrayMove(arrayOfImageUrls, oldIndex, newIndex);
		// For DOP decks: media_data.slides carries each slide's physical DOP
		// index/animation metadata at the same position as image_array — it must
		// move with the same oldIndex/newIndex or a slide's animations stay
		// behind at its old position while its thumbnail/content move. No-op for
		// non-DOP media, which never has `slides`. Aligned first so dragging an
		// added page (past the end of `slides`) moves its `null`, not a hole.
		const oldSlides = selectedMedia.media_data.slides;
		const newSlides = oldSlides
			&& arrayMove(pptxDeckAlignSlides(oldSlides, arrayOfImageUrls.length), oldIndex, newIndex);

		const updatedMedia = {
			...selectedMedia,
			images: newPages.join(','),
			media_data: {
				...selectedMedia.media_data,
				image_array: newPages,
				...(newSlides ? { slides: newSlides } : {}),
			},
			edited_by: user.gr_user,
		};

		// optimistically reflect the new order so the featured slide stays in sync
		// (the async save only updates the store once the API responds)
		dispatch(MediaActions.updateSingleMedia(updatedMedia));
		dispatch(MediaActions.saveMediaItem(token, updatedMedia, undefined));
		socket.setMedia(updatedMedia);

		dispatch(MediaActions.setSelectedPage(newPages.indexOf(currentlySelectedPage) + 1));
	};

	useEffect(() => {
		mediaRef.current = media;
	}, [media]);

	function onUploadInit(name: string, type: string, identifier: string, request: XMLHttpRequest) {
		addUpload(name, type, identifier, request);
		if (fileInput.current) {
			fileInput.current.value = '';
		}
	}

	async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
		// FYI: we modified the file input to only allow one file at
		// a time because of some corner cases that got brought up
		// with converting vs not converting high bitrate mp4s, CC
		// only allowing one conversion at a time, etc.
		const { files } = e.target;

		if (files && files.length > 0) {
			try {
				await doUploadMedia(
					true,
					files,
					onUploadInit,
				);
			} catch (error: any) {
				Error(error, undefined);
			}
		}
	}

	function clickUpload() {
		if (!isUploadingOrProcessing) { fileInput.current?.click(); }
	}

	function saveTitle(e: any) {
		if (
			selectedMedia
			&& token
			&& user
			&& checkFileName(e.target.value, selectedMedia.type === MEDIA_TYPES.HLS)
		) {
			dispatch(MediaActions.saveMediaItem(
				token,
				{ ...selectedMedia, custom_name: e.target.value, edited_by: user.gr_user },
				undefined,
			));
			socket?.setMedia({ ...selectedMedia, custom_name: e.target.value, edited_by: user.gr_user });
		}
	}

	async function deleteMediaItem() {
		if (!selectedMedia || !token) return;
		// selectedMedia.id is 1 if currently uploading directly to s3 (img/vid)
		if (selectedMedia.id === 1) {
			dispatch(MediaActions.setCurrentlyUploadingMedia(undefined));
			dispatch(MediaActions.setIdOfCurrentlyUploadingMedia(''));
		} else {
			// Converting or already converted through cloud convert.
			// Delete from SQL and Cloud Convert if job still exists
			if (currentlyUploadingInModal?.includes(selectedMedia.id)) {
				dispatch(MediaActions.setCurrentlyUploadingInModal(currentlyUploadingInModal.filter(
					(m) => m !== selectedMedia.id,
				)));
			}
			await DeleteMediaItem(token, selectedMedia.id);

			if (
				selectedMedia.cloud_convert_id
				&& selectedMedia.convert_status !== CLOUD_CONVERT_STATUS.COMPLETE
			) {
				await DeleteCloudConvertJob(token, selectedMedia.cloud_convert_id);
			}

			socket!.deleteMediaItem(selectedMedia!.id);

			stripFeedFromShots(
				selectedMedia!.id,
				AUDIO_TYPES.includes(selectedMedia.type) ? FEED_TYPES.MEDIA_AUDIO : FEED_TYPES.MEDIA,
				true,
			);
		}
		closeModal();
	}

	function deletePage(pageIndex: number, presentedPage?: number) {
		if (!selectedMedia || !socket || !user) return;

		// Save changes in the API (and then locally in the reducer when API returns)
		const newPages = selectedMedia.media_data.image_array;
		if (newPages.length > 1 && token) {
			// DOP decks: drop the page's `slides` entry too, or every later slide's
			// animations shift onto the wrong page.
			const oldSlides = selectedMedia.media_data.slides;
			const newSlides = oldSlides && pptxDeckAlignSlides(oldSlides, newPages.length)
				.filter((_, i) => i !== pageIndex);
			const removedPage = newPages.splice(pageIndex, 1);

			const updatedMedia = {
				...selectedMedia,
				images: newPages.join(','),
				num_pages: newPages.length,
				edited_by: user.gr_user,
				...(newSlides ? { media_data: { ...selectedMedia.media_data, slides: newSlides } } : {}),
			};

			// API uses this to delete image/vid in Amazon S3
			const pageDeleted = {
				location: removedPage[0].split('uploads/')[1],
			};
			dispatch(MediaActions.saveMediaItem(token, updatedMedia, pageDeleted));

			if (pageIndex === newPages.length) {
				// If user is on last page and deletes it, select page before
				// note pageindex is 0 indexed, but because newPages.splice got called
				dispatch(MediaActions.setSelectedPage(newPages.length));
				dispatch(MediaActions.setPresentedMediaPage(
					selectedMedia.id,
					newPages.length,
				));
			} else if (presentedPage && (pageIndex + 1) < presentedPage) {
				// page was behind presented page, now deck is 1 image shorter
				// need to decrement current page to keep it on the same image
				dispatch(MediaActions.setPresentedMediaPage(
					selectedMedia.id,
					presentedPage - 1,
				));
			}

			// Let other users know a media page was deleted.
			// If they are associated with the media item,
			// delete the page for them (app.tsx)
			socket.setMedia(updatedMedia);
		} else {
			deleteMediaItem();
		}
	}

	function showDeletePageDialog(pageIndex: number, currentPage?: number) {
		setDeletePageOpen(true);
		thumbnailToDeleteRef.current = document.getElementById(`thumbnail${pageIndex}`);
		deleteDialog.current.confirm(
			'Are you sure you want to delete this page?',
			'',
			'Delete',
			'Cancel',
			(confirmed: boolean) => {
				if (confirmed) {
					deletePage(pageIndex, currentPage);
				}
			},
		);
	}

	function deleteDialogXPixel() {
		if (thumbnailToDeleteRef.current) {
			if (deletePageOpen) {
				// delete specific page
				return thumbnailToDeleteRef.current.getBoundingClientRect().right + 15;
			}
			// delete full media item
			return deleteMediaItemBtnRef.current.getBoundingClientRect().left - 427;
		}
		return 0;
	}

	function deleteDialogYPixel() {
		if (thumbnailToDeleteRef.current) {
			if (deletePageOpen) {
			// delete specific page
				return thumbnailToDeleteRef.current.getBoundingClientRect().top - 35;
			}
			// delete full media item
			return deleteMediaItemBtnRef.current.getBoundingClientRect().top - 64;
		}
		return 0;
	}

	useEffect(() => {
		if (!pageUpload && !savingMedia) {
			setUploading(false);
		}
	}, [pageUpload, savingMedia]);

	const isCreatorOrProducer = useMemo(
		() => selectedMedia?.created_by === user?.gr_user
		|| selectedMedia?.created_by === user?.brandlive_profile
		|| host,
		[selectedMedia, user, host],
	);

	const [userCanEdit, userCanDelete] = useMemo(() => {
		// Decoupling migration strategy: include "brandlive_profile" here because
		// pre-decoupled media could have been created
		// by / shared with a "brandlive_profile" ID before "gr_user" IDs existed.
		if (user && isCreatorOrProducer) { return [true, true]; }

		if (
			user
			&& (
				selectedMedia?.shared_with?.includes(user.gr_user.toString())
				|| (
					user.brandlive_profile
					&& selectedMedia?.shared_with?.includes(user.brandlive_profile.toString())
				)
			)
		) {
			return [true, false];
		}

		return [false, false];
	}, [selectedMedia, user, host]);

	if (selectedMedia && (selectedMedia.num_pages > 0 || hasAnimations)) {
		keyListeners.useKey(keyListeners.KEYS.KEY_LEFT, previousPageClicked);
		keyListeners.useKey(keyListeners.KEYS.KEY_RIGHT, nextPageClicked);
	}

	const presentedMediaItem = useMemo(
		() => presentedMedia.find((p) => p.mediaId === selectedMedia?.id),
		[presentedMedia, selectedMedia],
	);

	return (
		<>
			<div className="media-modal">
				<div className="media-modal-top">
					<input ref={mediaTitleInputRef} type="text" onBlur={saveTitle}
						// Decoupling migration strategy: include "brandlive_profile" here
						// because pre-decoupled media could have been created
						// by a "brandlive_profile" ID before "gr_user" IDs existed.
						className={classNames('name', !isCreatorOrProducer && 'not-creator')}
						defaultValue={selectedMedia?.custom_name ?? selectedMedia?.original_name ?? ''}
						placeholder={isCreatorOrProducer ? 'Give me a name' : ''}
					/>

					<ButtonClose moreClassNames="close-modal-btn" onClick={() => closeModal()} />
					<div className="avatars">
						{arrayOfImageUrls.length > 0
							&& <ShareWith
								shareType={SHARE_TYPE.MEDIA}
								popoverToggled={shareWithPopoverToggled}
								socket={socket}
								popoverClosed={() => setShareWithPopoverOpen(false)}
								constrainToParent={true}
							/>
						}
					</div>
				</div>

				<div className="pages-container">
					<div className="pages" ref={pagesContainer}>
						{arrayOfImageUrls.length > 0
							? <DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragStart={onDragStart}
								onDragEnd={onDragEnd}
								onDragCancel={() => {
									setIsDraggingPage(false);
									setActivePageId(null);
								}}
								autoScroll={{
									// only auto-scroll vertically (x threshold 0 disables horizontal)
									threshold: { x: 0, y: 0.2 },
									// and only the pages list, never an ancestor/window
									canScroll: (element) => element === pagesContainer.current,
								}}
							>
								<SortableContext
									items={arrayOfImageUrls}
									strategy={verticalListSortingStrategy}
								>
									{arrayOfImageUrls.map((image, index) => {
										const url = selectedMedia?.media_data.thumbnail_map[image];
										const notCreator = (selectedMedia?.created_by !== user?.gr_user
											&& selectedMedia?.created_by !== user?.brandlive_profile)
											&& index === arrayOfImageUrls.length - 1;

										return <SortablePage
											key={image}
											id={image}
											index={index}
											thumbnailUrl={url}
											disabled={!isCreatorOrProducer}
											notCreator={!!notCreator}
											isUploading={mediaImagesCurrentlyUploading}
											uploadingLabel={isUploadingOrProcessing}
											fill={selectedMedia?.media_data.fill}
											backgroundColor={selectedMedia?.media_data.background_color}
											selected={selectedPage === index + 1}
											hovering={hoveringOverPageThumb === image}
											canDelete={userCanDelete}
											onClickPage={() => pageClicked(index + 1)}
											onMouseEnter={() => setHoveringOverPageThumb(image)}
											onMouseLeave={() => setHoveringOverPageThumb(undefined)}
											onDelete={() => showDeletePageDialog(index, presentedMediaItem?.currentPage)}
											thumbRef={(ref) => { pages.current[index] = ref; }}
										/>;
									})}
								</SortableContext>
								<DragOverlay className="dnd-grabbing-cursor">
									{activePageId ? (
										<div className="page">
											<div className={classNames('thumbnail')}>
												<div
													className={classNames('thumbnail-container', selectedMedia?.media_data.fill && 'fill')}
													style={{ background: selectedMedia?.media_data.background_color }}
												>
													<Thumbnail
														thumbnail={selectedMedia?.media_data.thumbnail_map[activePageId]}
													/>
												</div>
											</div>
										</div>
									) : null}
								</DragOverlay>
							</DndContext>
							:								<div className={'page'}>
								<div className={classNames('thumbnail')}>
									<div className="attachment-waiting">
										<WaitingIndicator />
									</div>
								</div>
							</div>
						}

						{userCanEdit && arrayOfImageUrls.length > 0 && selectedMedia?.type !== 'hls'
							&& <div className="page upload" onClick={clickUpload} ref={uploadButton}>
								<div className={classNames('thumbnail', 'upload')} ref={loadingThumbRef}>
									<div className="attachment-waiting">
										{isUploadingOrProcessing ? (
											<>
												{!!isUploadingOrProcessing && <span style={{ marginBottom: 15 }}>
													{isUploadingOrProcessing}
												</span>}
												<WaitingIndicator />
											</>
										) : (
											<span className="add-page">
												<Icon name={ICONS.ADD} color={COLORS.GRAY} size={14} />
												<span>Image or video</span>
											</span>
										)}
									</div>
									{isUploadingOrProcessing && (
										<div className="progress-wrap">
											<div className="progress-wipe" />
											<div
											className="progress-bar"
											style={{
												width: `${uploadProgress}%`,
											}}>

												</div>
										</div>
									)}
								</div>
							</div>
						}
						<input
							type="file"
							ref={fileInput}
							style={{ display: 'none' }}
							onChange={handleUpload}
							accept={[...VID_TYPES, ...IMG_TYPES]
								.map((a) => `.${a}`)
								.join(',')
							}
						/>
					</div>
				</div>
				<div
					className={classNames('featured-page', selectedMedia?.media_data.fill && 'fill', isLoaded ? '' : 'not-loaded')}
					style={{ background: selectedMedia?.media_data.background_color }}
				>
					{(hasAnimations && contentUrl) || currentImageUrl ? (
						<>
							{hasAnimations && contentUrl && (
								<MediaPresentationFrame
									contentUrl={contentUrl}
									mediaId={selectedMedia?.id ?? -1}
									currentPage={selectedPage}
									currentStep={selectedStep}
									onLoad={imageReady}
									onAutoAdvance={onPptxDeckAutoAdvance}
								/>
							)}
							{showsPageMedia && VID_TYPES.includes(getFileExtension(currentImageUrl)) && (
								<>
									<MediaVideo
										src={currentImageUrl}
										isHlsVideo={currentImageUrl.endsWith('.m3u8') && isPlayerSupported}
										onLoad={imageReady}
										ref={videoElRef}
										loop={!!selectedMedia?.media_data.loop}
										muted={true}
										crossOrigin={'anonymous'}
										controlsOn={true}
										autoPlay={true}
										onLoadedData={() => {
											setIsLoaded(true);
											send(LOADED, {
												video: videoElRef.current,
												audio: echoCancelledAudioRef.current,
												updatePresentedMedia: () => {},
												from: 'loading video',
											});

											send(PLAY, { doFireSync: false, from: 'autoplay' });
										}}
										onCanPlayThrough={() => {}}
										onPlay={() => {
											if (initialLoad.current) { start.current = window.performance.now(); }
										}}
										onTimeUpdate={() => {
											send(TIMING, { from: 'onTimeUpdate' });
											if (initialLoad.current && start.current) {
												initialLoad.current = false;
												const end = window.performance.now();
												const time = (end - start.current) / 1000;
												send(TIMECHANGE, { currentTime: time + elapsed });
											}
										}}
										onEnded={() => {
											send(END, { from: 'onEnded' });
										}}
										onError={() => {
											send(FAIL, { from: 'onError' });
										}}
										posterImage={BlackImage}
										elapsed={elapsed}
									/>
									<audio
										ref={echoCancelledAudioRef}
									></audio>
								</>
							)}
							{showsPageMedia && IMG_TYPES.includes(getFileExtension(currentImageUrl)) && (
								<>
									<img
										src={currentImageUrl}
										alt={''}
										onLoad={imageReady}
										onError={() => imageNotReady(currentImageUrl)}
										style={{ display: loadingIndicatorVisible ? 'none' : 'initial' }}
									/>
									{loadingIndicatorVisible && (
										<WaitingIndicator />
									)}
								</>
							)}
							{(hasAnimations ? deckTotal > 1 : arrayOfImageUrls.length > 1) && (
								<div className="switch-page-btn-wrapper">
									<div className="width-full position-absolute position-bottom flex-align-center justify-content-space-between padding-small">
										<button className={classNames('switch-page-btn', ((hasAnimations ? pptxAtStart : selectedPage === 1) || mediaImagesCurrentlyUploading) && 'disabled')} onClick={(e) => {
											previousPageClicked();
											e.currentTarget.blur();
										}}>
											<div style={{ marginRight: 1 }}>
												<Icon name={ICONS.KEYBOARD_ARROW_LEFT} color={COLORS.GRAY} size={12} />
											</div>
										</button>
										<div className="switch-page-counter">
											{showAnimationIcon
												&& <Icon className='margin-right-medium' name={ICONS.ANIMATION} size={16} color={COLORS.WHITE} />
											}
											{selectedPage}/{deckTotal}
										</div>
										<button className={classNames('switch-page-btn', ((hasAnimations ? pptxAtEnd : selectedPage === arrayOfImageUrls.length) || mediaImagesCurrentlyUploading) && 'disabled')} onClick={(e) => {
											nextPageClicked();
											e.currentTarget.blur();
										}}>
											<div style={{ marginLeft: 3 }}>
												<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} color={COLORS.GRAY} size={12} />
											</div>
										</button>
									</div>
								</div>
							)}
						</>
					)
						: <WaitingIndicator />
					}
				</div>
				{host && (
					<MediaModalButtons
						socket={socket}
						selectedMedia={selectedMedia}
						arrayOfImageUrls={arrayOfImageUrls}
					/>
				)}
				{
					shareWithPopoverOpen
					&& <div className="modal-overlay" />
				}
			</div >
			<Dialog
				ref={deleteDialog}
				xPixel={deleteDialogXPixel()}
				yPixel={deleteDialogYPixel()} />
			<Dialog
				right={true}
				ref={bitrateDialogRef}
				xPixel={
					(loadingThumbRef.current?.getBoundingClientRect().right ?? 0) + 15
				}
				yPixel={
					(loadingThumbRef.current?.getBoundingClientRect().top ?? 0) - 35
				}
			/>
		</>
	);
}
